import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  cn,
  HelperText,
  Modal,
  ModalActions,
  ModalContent,
  ModalTitleInverse,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useSnackbar,
} from '@c2fo/liquidity';
import { computeMarketSubmitStats, useRateTypeOptions } from '@/enterprise/components/OfferBuilder/utils';
import Skeleton from '@/enterprise/components/Skeleton';
import { useConfirmAgreementsWhenRequired } from '@/enterprise/data/useAgreements';
import useSubmitExpressAcceptOffer from '@/enterprise/data/useSubmitExpressAcceptOffer';
import useSubmitPriceDiscoveryOffer from '@/enterprise/data/useSubmitPriceDiscoveryOffer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { Agreement } from '@/enterprise/features/agreements/Agreement';
import { validateRateInputForTakerMarket } from '@/enterprise/features/offerModal/utils';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useExchangeRates from '@/shared/data/useExchangeRates';
import getTakerMarketName from '@/utils/getTakerMarketName';
import { getNormalizedRate } from '@/utils/validateRate';
import PricingStrategyOptions from './PricingStrategyOptions';
import { SelectedStrategy } from './utils';

interface RateSelectModalProps {
  open: boolean;
  takerMarket: TakerMarket;
  onClose: () => void;
}

const EditRateModalSkeleton = () => {
  return (
    <ModalContent className="pt-2">
      <div className="space-y-4">
        <Skeleton className="h-14 w-3/5" shadow={false} />
        <div className="space-y-4">
          <Skeleton className="h-20 w-full rounded-xl" shadow={false} />
          <Skeleton className="h-20 w-full rounded-t-xl" shadow={false} />
        </div>
      </div>
    </ModalContent>
  );
};

const EditRateModalContent = ({ open, takerMarket, onClose }: RateSelectModalProps) => {
  const { t } = useTranslation();
  const { addError } = useDataDog();
  const showSnackbar = useSnackbar();
  const rateOptions = useRateTypeOptions();
  const { trackEnterpriseEvent } = useReporting();
  const { data: exchangeRates } = useExchangeRates();
  const { validateAndConfirmAgreementsConditionally } = useConfirmAgreementsWhenRequired([takerMarket]);
  const initialRateType = takerMarket.offerConfig.isDiscountBidding ? 'DISC' : 'APR';
  const [selectedRateType, setSelectedRateType] = useState<'APR' | 'DISC'>(initialRateType);
  const [selectedStrategy, setSelectedStrategy] = useState<SelectedStrategy | null>(null);
  const [rateError, setRateError] = useState<string | null>(null);
  const [isAgreementsChecked, setIsAgreementsChecked] = useState(false);
  const [agreementError, setAgreementError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const { mutateAsync: submitExpressAcceptOffer, isPending: isSubmittingExpressAccept } = useSubmitExpressAcceptOffer();
  const { mutateAsync: submitPriceDiscoveryOffer, isPending: isSubmittingPriceDiscovery } =
    useSubmitPriceDiscoveryOffer();
  const isSubmitting = isSubmittingExpressAccept || isSubmittingPriceDiscovery;

  // initialize rate type when modal opens
  useEffect(() => {
    if (open && takerMarket.offerConfig) {
      const currentRateType = takerMarket.offerConfig.isDiscountBidding ? 'DISC' : 'APR';
      setSelectedRateType(currentRateType);
      setSelectedStrategy(null);
      setRateError(null);
      setIsAgreementsChecked(false);
      setAgreementError(null);
    }
  }, [open, takerMarket]);

  const handleRateTypeChange = (newRateType: 'APR' | 'DISC') => {
    setSelectedRateType(newRateType);
    setSelectedStrategy(null);
    setRateError(null);

    trackEnterpriseEvent('edit-rate-modal::rate-type-changed', {
      oldRateType: selectedRateType,
      newRateType,
      marketUuid: takerMarket.marketUuid,
    });
  };

  const handleSelectStrategy = (strategy: SelectedStrategy) => {
    setSelectedStrategy(strategy);
    setRateError(null);

    trackEnterpriseEvent('edit-rate-modal::strategy-selected', {
      strategyType: strategy.type,
      rate: strategy.rate,
      rateType: strategy.rateType,
      marketUuid: takerMarket.marketUuid,
    });
  };

  // Validate rate and return error message if invalid
  const validateRate = (): { error: string | null; rate: number; strategy: SelectedStrategy | null } => {
    if (!selectedStrategy) {
      return { error: t('offerSubmitDialog.errors.enterAValidRate'), rate: 0, strategy: null };
    }

    if (takerMarket.marketPricingType !== 'PRICE_DISCOVERY') {
      const error = new Error(`Unsupported market pricing type: ${takerMarket.marketPricingType}`);
      addError('Rate Select Modal - Invalid Market Pricing Type', {
        error,
        marketPricingType: takerMarket.marketPricingType,
        takerMarket: {
          marketUuid: takerMarket.marketUuid,
          takerDivisionUuid: takerMarket.takerDivisionUuid,
        },
      });
      return { error: t('offerSubmitDialog.offerSetError'), rate: 0, strategy: null };
    }

    // Reason: a typed or slider rate can carry more precision than the 2 decimals shown in the input,
    // so round it the same way Offer Builder does before it is submitted and tracked.
    // Suggested rates (trending, express accept) are submitted as pricing returned them, matching Offer Builder.
    const rate =
      selectedStrategy.type === 'nameYourRate'
        ? getNormalizedRate(String(selectedStrategy.rate))
        : selectedStrategy.rate;

    // rate === 0 typically means empty input, show generic message
    if (rate === 0) {
      return { error: t('offerSubmitDialog.errors.enterAValidRate'), rate, strategy: null };
    }

    if (rate < 0) {
      return { error: t('offerSubmitDialog.errors.enterRateGreaterThanZero'), rate, strategy: null };
    }

    const { errorMessage } = validateRateInputForTakerMarket({
      rateInput: String(rate),
      rateType: selectedRateType === 'DISC' ? 'disc' : 'apr',
      takerMarket,
      t,
    });

    return { error: errorMessage || null, rate, strategy: errorMessage ? null : selectedStrategy };
  };

  // Validate agreements and return true if there's an error
  const validateAgreements = async (): Promise<boolean> => {
    try {
      await validateAndConfirmAgreementsConditionally({ isAgreementsChecked });
      return false; // No error
    } catch {
      return true; // Has error
    }
  };

  const handleSave = async () => {
    // Clear previous errors
    setRateError(null);
    setAgreementError(null);

    // Run validations (synchronous rate validation, async agreement validation)
    setIsValidating(true);
    const { error: rateValidationError, rate, strategy: validatedStrategy } = validateRate();
    const hasAgreementError = await validateAgreements();
    setIsValidating(false);

    // Set errors if any validation failed
    if (rateValidationError) {
      setRateError(rateValidationError);
    }

    if (hasAgreementError) {
      setAgreementError(t('agreements.error.checkbox'));
    }

    // Stop if any validation failed
    if (rateValidationError || hasAgreementError || !validatedStrategy) {
      return;
    }

    const submitOffer =
      validatedStrategy.type === 'expressAccept'
        ? submitExpressAcceptOffer({
            rate,
            rateType: selectedRateType,
            makerOrganizationUuid: takerMarket.makerOrganizationUuid,
            marketUuid: takerMarket.marketUuid,
            supplierDivisionUuid: takerMarket.takerDivisionUuid,
            referrer: { workflow: 'OFFER_ACTIVITY' },
          })
        : submitPriceDiscoveryOffer({
            marketUuid: takerMarket.offerConfig.marketUuid,
            offerConfig: {
              uuid: takerMarket.offerConfig.uuid,
              isDiscountBidding: selectedRateType === 'DISC',
              maxDiscount: selectedRateType === 'DISC' ? rate : takerMarket.offerConfig.maxDiscount,
              maxApr: selectedRateType === 'APR' ? rate : takerMarket.offerConfig.maxApr,
              exclusionSettings: takerMarket.offerConfig.exclusionSettings,
              expireOn: null,
              schedule: takerMarket.offerSchedule,
              frequency: takerMarket.offerConfig.frequency,
            },
            supplierDivisionUuid: takerMarket.takerDivisionUuid,
            referrer: { workflow: 'OFFER_ACTIVITY' },
          });

    try {
      await submitOffer;

      const marketStats = computeMarketSubmitStats({
        takerMarket,
        rate,
        rateType: selectedRateType,
        exchangeRates,
      });
      trackEnterpriseEvent('offer::submitted', {
        rate,
        rateType: selectedRateType,
        pricingStrategy: validatedStrategy.type,
        marketUuid: takerMarket.marketUuid,
        supplierDivisionUuid: takerMarket.takerDivisionUuid,
        referrer: 'edit-rate-modal',
        isDraft: false,
        isEdit: true,
        isEnabled: true,
        isManuallyAwarded: takerMarket.offerConfig.isManuallyAwarded,
        isPreferred: takerMarket.offerConfig.offerType === 'PREFERRED_TERM',
        ...marketStats,
      });

      showSnackbar({
        message: t('editRateModal.rateSaved'),
      });

      onClose();
    } catch (error) {
      addError('Rate Select Modal Save Error', {
        error,
        takerMarket: {
          marketUuid: takerMarket.marketUuid,
          takerDivisionUuid: takerMarket.takerDivisionUuid,
        },
        selectedStrategy: validatedStrategy,
        rate,
      });

      showSnackbar({
        message: t('editRateModal.saveError'),
      });
    }
  };

  const rateTypeLabel = selectedRateType === 'APR' ? t('core.apr') : t('core.discountAbbreviation');
  const frequency: 'oneTime' | 'recurring' = takerMarket.offerConfig.frequency === 'ONE_TIME' ? 'oneTime' : 'recurring';

  // Determine error display state for pricing options
  const hasNoSelectionError = !selectedStrategy && !!rateError;

  return (
    <>
      <ModalContent className="pt-2">
        <div className="space-y-4">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div
              className={cn('text-sm font-medium text-text-secondary', {
                'text-red-600': hasNoSelectionError,
              })}
            >
              {t('builder.selectYourRate')}
            </div>
            <div>
              <Select value={selectedRateType} onValueChange={handleRateTypeChange}>
                <SelectTrigger aria-label={t('core.rateType')} className="w-full md:w-fit" size="sm">
                  <span className="flex items-center gap-1">
                    <span className="text-text-secondary">{t('core.rateType')}:</span>
                    <span className="font-medium">
                      <SelectValue />
                    </span>
                  </span>
                </SelectTrigger>
                <SelectContent align="end" className="min-w-full md:min-w-80">
                  {rateOptions.map((rateOption) => (
                    <SelectItem key={rateOption.value} value={rateOption.value}>
                      <p>{rateOption.label}</p>
                      <p className="block text-wrap text-sm [button_&]:hidden">{rateOption.desc}</p>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <PricingStrategyOptions
            selectedStrategy={selectedStrategy}
            onSelectStrategy={handleSelectStrategy}
            rateTypeLabel={rateTypeLabel}
            selectedRateType={selectedRateType}
            errorMessage={rateError}
            hasNoSelectionError={hasNoSelectionError}
            onClearError={() => setRateError(null)}
            takerMarket={takerMarket}
            frequency={frequency}
            open={open}
          />
          <div className="pt-2">
            <div className={cn('rounded-lg border border-transparent p-2', { 'border-red-600': agreementError })}>
              <Agreement
                takersMarkets={[takerMarket]}
                onChange={(v) => {
                  setIsAgreementsChecked(v);
                  setAgreementError(null);
                }}
              />
            </div>
            {agreementError && (
              <div className="pt-2">
                <HelperText variant="error">{t('builder.agreementsErrorMessage')}</HelperText>
              </div>
            )}
          </div>
        </div>
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={onClose} disabled={isValidating || isSubmitting}>
          {t('core.cancel')}
        </Button>
        <Button variant="cta" onClick={handleSave} disabled={isValidating || isSubmitting} loading={isSubmitting}>
          {t('core.submit')}
        </Button>
      </ModalActions>
    </>
  );
};

const EditRateModal = ({ open, takerMarket, onClose }: RateSelectModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal open={open} onClose={onClose} className="sm:w-[600px]">
      <ModalTitleInverse>
        <span className="flex flex-col">
          <span>{t('builder.editRate')}</span>
          <span className="text-sm font-normal text-text-secondary">{getTakerMarketName(takerMarket)}</span>
        </span>
      </ModalTitleInverse>
      <QueryBoundaries LoadingComponent={EditRateModalSkeleton}>
        <EditRateModalContent open={open} takerMarket={takerMarket} onClose={onClose} />
      </QueryBoundaries>
    </Modal>
  );
};

export default EditRateModal;
